import React, { useEffect, useState } from "react";
import { api } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { PersonBanner, KIND_LABEL, type PersonHeader } from "./Otc";
import { stamp, DISPOSITION_LABEL } from "./Pass";
import { FLAG_LABEL } from "./ReviewQueue";

/**
 * The chart (build plan §10.1 #5): one page per person, newest first —
 * visits, administrations (Rx and OTC), progress notes (append-only), flags
 * and the activity restriction, under the allergy banner. Reached from the
 * Med station, Record review, the pass and the visit.
 */

export interface RestrictionView {
  text: string;
  untilOn: string;
  active: boolean;
}
type Entry =
  | { type: "visit"; atUtc: string; visitKey: string; kind: string; complaint: string; disposition: string; status: string; versions: number; recordedBy: string }
  | { type: "administration"; atUtc: string; adminKey: string; kind: string; medicationName: string; disposition: string; amountGiven: string; by: string; reason: string; countersigned: string; visitKey: string }
  | { type: "note"; atUtc: string; noteKey: string; body: string; author: string };
interface ChartView {
  person: PersonHeader & { restriction: RestrictionView | null };
  flags: { type: string; reasons: string[] }[];
  entries: Entry[];
  can: { note: boolean; restrict: boolean; visit: boolean; otc: boolean };
}

/** The restriction as the pass, Give now and the chart show it. */
export function RestrictionBanner({ r }: { r: RestrictionView | null | undefined }) {
  if (!r?.active) return null;
  return (
    <div className="mb-3 rounded-md border-2 border-orange-500 bg-orange-50 px-3 py-2 text-sm text-orange-950" role="note">
      <span className="font-bold">ACTIVITY RESTRICTION</span>{r.untilOn ? ` (until ${r.untilOn})` : ""}: {r.text}
    </div>
  );
}

function RestrictionForm({ v, onSaved }: { v: ChartView; onSaved: (c: ChartView) => void }) {
  const r = v.person.restriction;
  const [text, setText] = useState(r?.text ?? "");
  const [untilOn, setUntilOn] = useState(r?.untilOn ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { problems, check } = useProblems([["rs-until", Boolean(text.trim()) && !untilOn, "Give the restriction's end date."]]);
  async function save(clear: boolean) {
    setError(null);
    if (!clear && !check()) return;
    setBusy(true);
    const x = await api<{ chart: ChartView }>("PUT", `/api/chart/${encodeURIComponent(v.person.personKey)}/restriction`, clear ? { text: "" } : { text, untilOn });
    setBusy(false);
    if (!x.ok) return setError(x.data.error ?? "Not saved.");
    onSaved(x.data.chart);
  }
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_12rem]">
      <div><label htmlFor="rs-text" className="field-label">Restriction (what the student may not do)</label><input id="rs-text" className="field-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="No contact sports; walk, don't run" /></div>
      <div><label htmlFor="rs-until" className="field-label">Until (end date)</label><input id="rs-until" type="date" className="field-input" {...invalid("rs-until", problems)} value={untilOn} onChange={(e) => setUntilOn(e.target.value)} /><FieldProblem id="rs-until" problems={problems} /></div>
      <div className="flex flex-wrap gap-2 sm:col-span-2">
        <button type="button" className="btn-secondary min-h-11" disabled={busy || !text.trim()} onClick={() => void save(false)}>{r?.text ? "Save the restriction" : "Set the restriction"}</button>
        {r?.text && <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => void save(true)}>End the restriction</button>}
      </div>
      {error && <p className="field-error sm:col-span-2" role="alert">{error}</p>}
    </div>
  );
}

function NoteForm({ v, onSaved }: { v: ChartView; onSaved: (c: ChartView) => void }) {
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { problems, check } = useProblems([["pn-body", !body.trim(), "Write the progress note."]]);
  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const x = await api<{ chart: ChartView }>("POST", `/api/chart/${encodeURIComponent(v.person.personKey)}/notes`, { body });
    setBusy(false);
    if (!x.ok) return setError(x.data.error ?? "Not saved.");
    setBody("");
    onSaved(x.data.chart);
  }
  return (
    <div>
      <label htmlFor="pn-body" className="field-label">Progress note (kept as written; add another to correct it)</label>
      <textarea id="pn-body" className="field-input min-h-20" {...invalid("pn-body", problems)} value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} />
      <FieldProblem id="pn-body" problems={problems} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void save()}>Add the note</button>
    </div>
  );
}

export function Chart({ personKey }: { personKey: string }): React.ReactElement {
  const [v, setV] = useState<ChartView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<{ chart: ChartView }>("GET", `/api/chart/${encodeURIComponent(personKey)}`).then((r) => (r.ok ? setV(r.data.chart) : setError(r.data.error ?? "Could not load.")));
  }, [personKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!v) return <p className="text-sm text-slate-600">Loading…</p>;
  const p = v.person;
  const key = encodeURIComponent(p.personKey);
  return (
    <div>
      <p className="mb-3 flex flex-wrap gap-x-3 text-sm">
        {p.personType === "Student" && <a href={`/station/${key}`} className="text-program underline">Med station</a>}
        {p.personType === "Student" && <a href={`/review/${key}`} className="text-program underline">Record review</a>}
        {p.personType === "Student" && <a href={`/pass/student/${key}`} className="text-program underline">Give now</a>}
        {v.can.otc && <a href={`/otc/${key}`} className="text-program underline">Give an OTC item</a>}
        {v.can.visit && !p.departed && <a href={`/visits/new/${key}`} className="text-program underline">Start a visit</a>}
      </p>
      <PersonBanner p={p} />
      <RestrictionBanner r={p.restriction} />
      <section className="section-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-semibold">Chart: {p.lastName}, {p.firstName}</h2>
          <span className="text-sm text-slate-600">{p.personKey}{p.dateOfBirth ? ` · born ${p.dateOfBirth}` : ""}{p.personType === "Staff" ? " · staff" : ` · ${p.city || "Not assigned"}`}</span>
        </div>
        {p.departed && <p className="mt-1 text-sm font-semibold text-red-800">Left the session.</p>}
        {v.flags.length > 0 && <p className="mt-1 flex flex-wrap gap-1 text-xs">{v.flags.map((f) => <span key={f.type} className="rounded bg-slate-200 px-1.5 py-0.5">{FLAG_LABEL[f.type] ?? f.type}{f.reasons.length ? `: ${f.reasons.join(", ")}` : ""}</span>)}</p>}
        {v.can.restrict && (
          <div className="mt-3">
            <p className="text-sm font-semibold">Activity restriction / medical hold</p>
            {p.restriction?.text && !p.restriction.active && <p className="text-xs text-slate-500">Ended {p.restriction.untilOn}: {p.restriction.text}</p>}
            <RestrictionForm v={v} onSaved={setV} />
          </div>
        )}
      </section>
      {v.can.note && <section className="section-card"><NoteForm v={v} onSaved={setV} /></section>}
      <section className="section-card">
        <h3 className="mb-2 text-base font-semibold">Newest first</h3>
        {v.entries.length === 0 ? <p className="text-sm text-slate-600">Nothing yet.</p> : (
          <ol className="space-y-2">
            {v.entries.map((e) => (
              <li key={e.type === "visit" ? e.visitKey : e.type === "note" ? e.noteKey : e.adminKey} className="rounded-md border border-slate-200 p-2 text-sm">
                <span className="text-xs text-slate-500">{stamp(e.atUtc)}</span>
                {e.type === "visit" && (
                  <p><span className="mr-1 rounded bg-sky-100 px-1.5 text-xs font-semibold text-sky-900">Visit</span><a className="font-medium text-program underline" href={`/visits/${encodeURIComponent(e.visitKey)}`}>{KIND_LABEL[e.kind] ?? e.kind}: {e.complaint}</a> · {e.status}{e.disposition ? ` · ${e.disposition}` : ""}{e.versions > 1 ? " · corrected — see history" : ""} · {e.recordedBy}</p>
                )}
                {e.type === "administration" && (
                  <p><span className={`mr-1 rounded px-1.5 text-xs font-semibold ${e.kind === "OTC" ? "bg-emerald-100 text-emerald-900" : "bg-violet-100 text-violet-900"}`}>{e.kind}</span>{e.medicationName} · {DISPOSITION_LABEL[e.disposition] ?? e.disposition}{e.amountGiven ? ` · ${e.amountGiven}` : ""} · {e.by}{e.reason ? ` · ${e.reason}` : ""}{e.countersigned === "N" ? " · unverified" : ""}</p>
                )}
                {e.type === "note" && (
                  <p><span className="mr-1 rounded bg-slate-200 px-1.5 text-xs font-semibold">Note</span><span className="whitespace-pre-wrap">{e.body}</span> <span className="text-xs text-slate-500">— {e.author}</span></p>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
